<template lang="pug">
span
  control(v-if="autofill.allowed")
    field
      input(v-model="autofill.filled", :id="autofill.id", :type="'checkbox'")
      label(:for="autofill.id") {{ autofill.label }}

  control
    field(:class="{ invalid: !street.meta.valid && street.meta.touched }")
      label(:for="street.id")(v-html="street.label")
      input(
        @focus="googleAutoComplete.onFocus()",
        @blur="street.blur()",
        @keypress.enter.stop="",
        v-model="street.model",
        :type="street.type",
        :name="street.id",
        :id="street.id",
        :placeholder="street.placeholder",
        ref="street_input"

      )
      error(v-if="!street.meta.valid && street.meta.touched") {{ street.errorMessage }}

  control
    group
      field(:class="{ invalid: !city.meta.valid && city.meta.touched }")
        label(:for="city.id")(v-html="city.label")
        input(
          @blur="city.blur",
          v-model="city.model",
          :type="city.type",
          :name="city.id",
          :id="city.id",
          :placeholder="city.placeholder"
        )
        error(v-if="!city.meta.valid && city.meta.touched") {{ city.errorMessage }}

      dropdown(:field="state.field", :nested="true")

      field(:class="{ invalid: !zip.meta.valid && zip.meta.touched }")
        label(:for="zip.id")(v-html="zip.label")
        input(
          @blur="zip.blur",
          v-model="zip.model",
          :type="zip.type",
          :name="zip.id",
          :id="zip.id",
          :placeholder="zip.placeholder"
        )
        error(v-if="!zip.meta.valid && zip.meta.touched") {{ zip.errorMessage }}
</template>

<script>
import stateList from "../../assets/data/statelist.json";
import Dropdown from "./Dropdown.vue";
import { useStore } from "vuex";
import { reactive, computed, watch, ref, onMounted, nextTick } from "vue";
import { useField } from "vee-validate";
import { cloneDeep } from "lodash-es";
import * as yup from "yup";

const ADDRESS_COMPONENTS = {
  street_number: "long_name",
  route: "short_name",
  administrative_area_level_1: "short_name",
  locality: "long_name",
  postal_code: "short_name",
};

export default {
  name: "FullAddress",
  props: {
    field: Object,
  },
  components: { dropdown: Dropdown },
  setup(p) {
    const store = useStore();
    const street = reactive(initStreet());
    const city = reactive(initCity());
    const state = reactive(initState());
    const zip = reactive(initZip());
    const autocomplete = reactive({ data: null });
    const autofill = reactive(initAutofill());
    const googleAutoComplete = reactive(initGoogleAutocomplete());
    const street_input = ref(null);
    let previousResults;

    return {
      street,
      city,
      state,
      zip,
      autofill,
      googleAutoComplete,
      street_input,
    };

    function initGoogleAutocomplete() {
      const geolocation = reactive({
        geocoder: null,
        loc: null,
        position: null,
      });

      onMounted(async () => {
        await nextTick();
        await window.mapInitialized;
        if (
          window.google &&
          window.google.maps &&
          window.google.maps.places &&
          window.google.maps.places.Autocomplete
        ) {
          const _autocomplete = new window.google.maps.places.Autocomplete(
            street_input.value,
            {
              types: ["address"],
              componentRestrictions: { country: "us" },
            }
          );
          _autocomplete.setFields(["address_component"]);
          _autocomplete.addListener("place_changed", onPlaceChanged);
          autocomplete.data = _autocomplete;
        }
      });

      const onFocus = () => {
        biasAutocompleteLocation();
      };

      return {
        autocomplete,
        onFocus,
      };

      function updateGeolocation(callback = null) {
        if (window.__locationRequested === undefined) window.__locationRequested = false;
        if (window.__locationRequested === true) return;
        else window.__locationRequested = true;
        if (navigator.geolocation) {
          navigator.geolocation.getCurrentPosition(
            (position) => {
              let _geolocation = {
                lat: position.coords.latitude,
                lng: position.coords.longitude,
              };
              geolocation.loc = _geolocation;
              geolocation.position = position;
              if (callback) callback(_geolocation, position);
            },
            (err) => {
              console.log(
                "error",
                "Cannot get Coordinates from navigator",
                err
              );
            },
            {}
          );
        }
      }

      function biasAutocompleteLocation() {
        updateGeolocation((geolocation, position) => {
          let circle = new window.google.maps.Circle({
            center: geolocation,
            radius: position.coords.accuracy,
          });
          autocomplete.data.setBounds(circle.getBounds());
        });
      }

      function formatResult(place) {
        let returnData = {};
        for (let i = 0; i < place.address_components.length; i++) {
          let addressType = place.address_components[i].types[0];
          if (ADDRESS_COMPONENTS[addressType]) {
            let val =
              place.address_components[i][ADDRESS_COMPONENTS[addressType]];
            returnData[addressType] = val;
          }
        }
        return returnData;
      }

      function onPlaceChanged() {
        const place = autocomplete.data.getPlace();
        if (place.address_components !== undefined) {
          let results = formatResult(place);
          if (previousResults) results = { ...previousResults, ...results };
          street.model = "";
          city.model = "";
          state.field.model = "";
          zip.model = "";

          setTimeout(() => {
            street.model = `${results.street_number} ${results.route}`;
            city.model = results.locality;
            state.field.model = stateList.find(
              ([val]) =>
                val.toLowerCase() ===
                results.administrative_area_level_1.toLowerCase()
            )[0];
            zip.model = results.postal_code;
            previousResults = results;
            setTimeout(() => {
              document.activeElement.blur();
            }, 10);
          }, 25);
        }
      }
    }

    function initAutofill() {
      const autofill = p.field.autofill || { allowed: false };

      autofill.id = `${p.field.id}-autofill`;

      if (autofill.allowed) {
        autofill.filled =
          street.model &&
          street.model === store.getters.model[`${autofill.from}`] &&
          city.model &&
          city.model === store.getters.model[`${autofill.from}-city`] &&
          state.model &&
          state.model === store.getters.model[`${autofill.from}-state`] &&
          zip.model &&
          zip.model === store.getters.model[`${autofill.from}-zip`];
      }

      watch(
        () => autofill.filled,
        (filled) => {
          if (filled) {
            street.model = store.getters.model[`${autofill.from}`];
            city.model = store.getters.model[`${autofill.from}-city`];
            state.field.model = store.getters.model[`${autofill.from}-state`];
            zip.model = store.getters.model[`${autofill.from}-zip`];
          } else {
            street.model = "";
            city.model = "";
            state.field.model = "";
            zip.model = "";
          }
        }
      );

      return autofill;
    }

    function initStreet() {

      let labelHtml = "Address";
      if (p.field.required !== false) labelHtml = `<b>${labelHtml}</b>`;

      const id = p.field.id;
      const label = `${labelHtml}${p.field.required !== false ? " <span class='required'>*</span>" : ""}`;
      const type = "text";
      const streetErrorMessage = "Please enter a valid street address";

      const rules =
        p.field.required === false
          ? yup.string()
          : yup
            .string()
            .trim()
            .required()
            .matches(/\w+(\s\w+)/, streetErrorMessage);

      const { value, errorMessage, meta, handleBlur, validate } = useField(
        id,
        rules,
        {
          validateOnInput: true,
          initialValue: store.getters.model[id],
        }
      );

      const model = computed({
        get: () => store.getters.model[id],
        set: (v) => {
          value.value = v;
          store.commit("updateModel", { id, value: v });
        },
      });

      const blur = async () => {
        store.commit("pushLastFieldToDataLayer", id);
        await validate();
        handleBlur();
      };

      const placeholder = p.field.required === false
        ? ""
        : "(Required)";

      return { id, label, type, model, meta, blur, errorMessage, placeholder };
    }

    function initCity() {

      let labelHtml = "City";
      if (p.field.required !== false) labelHtml = `<b>${labelHtml}</b>`;

      const id = `${p.field.id}-city`;
      const label = `${labelHtml}${p.field.required !== false ? " <span class='required'>*</span>" : ""}`;
      const type = "text";

      const rules =
        p.field.required === false
          ? yup.string()
          : yup.string().trim().required().min(2);

      const { value, errorMessage, meta, handleBlur, validate } = useField(
        id,
        rules,
        {
          validateOnInput: true,
          initialValue: store.getters.model[id],
        }
      );

      const model = computed({
        get: () => store.getters.model[id],
        set: (v) => {
          value.value = v;
          store.commit("updateModel", { id, value: v });
        },
      });

      const blur = async () => {
        store.commit("pushLastFieldToDataLayer", id);
        await validate();
        handleBlur();
      };

      const placeholder = p.field.required === false
        ? ""
        : "(Required)";

      return { id, label, type, model, meta, blur, errorMessage, placeholder };
    }

    function initState() {
      const field = {
        id: `${p.field.id}-state`,
        label: "State",
        multi: false,
        values: cloneDeep(stateList),
        required: p.field.required,
        model: undefined,
      };

      return {
        field,
      };
    }

    function initZip() {

      let labelHtml = "Zip Code";
      if (p.field.required !== false) labelHtml = `<b>${labelHtml}</b>`;

      const id = `${p.field.id}-zip`;
      const label = `${labelHtml}${p.field.required !== false ? " <span class='required'>*</span>" : ""}`;
      const type = "text";
      const zipErrorMessage = "Please enter a valid 5 diget zip code";

      const rules =
        p.field.required === false
          ? yup.string()
          : yup
            .string()
            .required()
            .matches(/[0-9]{5}/, zipErrorMessage)
            .min(5)
            .max(5);

      const { value, errorMessage, meta, handleBlur, validate } = useField(
        id,
        rules,
        {
          validateOnInput: true,
          initialValue: store.getters.model[id],
        }
      );

      const model = ref(/*fixed*/);
      const initial = ref(true);

      watch(
        () => store.getters.model[id],
        (v) => (model.value = v)
      );

      watch(model, (v) => {
        if (Object.hasOwn(store.getters.model, id) || initial.value) {
          if (!v) v = "";
          v = v.replace(/\D/gm, "").substr(0, 5);
          value.value = v;
          if (model.value !== v) {
            model.value = v || "";
          }
          initial.value = false;
          store.commit("updateModel", { id: id, value: v });
        }
      });

      model.value = store.getters.model[id] || "";

      const blur = async () => {
        store.commit("pushLastFieldToDataLayer", id);
        await validate();
        handleBlur();
      };

      const placeholder = p.field.required === false
        ? ""
        : "(Required)";

      return { id, label, type, model, meta, blur, errorMessage, placeholder };
    }
  },
};
</script>
  
<style lang="scss" scoped>
span {
  display: contents;
}

::v-deep(form-group) {
  flex: 1 0 100% !important;

  &>form-field:first-child {
    flex: 2 1 0;
  }
}
</style>
